if (!customElements.get('ui-select')) {
  const svgHTML = `
  <svg aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7" fill="none">
  <path d="M10 1.5L6.46447 5.03553C6.07394 5.42606 5.44078 5.42606 5.05025 5.03553L1.51472 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

  class UISelect extends HTMLElement {
    /** @type {HTMLButtonElement} */
    // @ts-expect-error  This is set in connectedCallback, but helps avoid JSDoc errors
    trigger;

    /** @type {HTMLInputElement} */
    // @ts-expect-error  This is set in connectedCallback, but helps avoid JSDoc errors
    hiddenInput;

    constructor() {
      super();
      this.addEventListener('click', this.onClick.bind(this));
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }

    connectedCallback() {
      if (this.getAttribute('data-initialized') === 'true') {
        return;
      }

      const baseId = this.newId();
      const triggerId = baseId + '--trigger';
      const itemsId = baseId + '--items';

      const labelText = this.getAttribute('data-label') || 'Select';
      /**
       * @type {HTMLElement[]}
       */
      const items = Array.from(this.querySelectorAll('ui-select-item'));

      const header = document.createElement('div');
      header.classList.add('ui-select__header');

      const selectedItem =
        items.find(item => item.hasAttribute('selected')) || items[0];

      const trigger = document.createElement('button');
      trigger.classList.add('ui-select__selected');
      trigger.innerHTML = selectedItem.innerHTML;
      trigger.id = triggerId;
      trigger.role = 'combobox';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', itemsId);
      trigger.setAttribute('aria-label', labelText);

      const itemsContainer = document.createElement('div');
      itemsContainer.classList.add('ui-select__items');
      itemsContainer.id = itemsId;
      itemsContainer.role = 'listbox';

      let selectedValue = '';

      let i = 0;
      for (const item of items) {
        const div = document.createElement('div');
        div.classList.add('ui-select__item');
        div.role = 'option';
        div.innerHTML = item.innerHTML;

        if (i === 0) {
          div.setAttribute('aria-selected', 'true');
          selectedValue = item.getAttribute('value') || '';
        } else {
          div.setAttribute('aria-selected', 'false');
        }
        div.setAttribute('value', item.getAttribute('value') || '');
        div.tabIndex = -1;

        if (item.hasAttribute('selected')) {
          this.setAttribute('value', item.getAttribute('value') || '');
        }

        itemsContainer.appendChild(div);
        i++;
      }

      const svgIcon = document.createElement('span');
      svgIcon.classList.add('ui-select__icon');
      svgIcon.innerHTML = svgHTML;

      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.value = selectedValue;

      if (this.dataset.name) {
        hiddenInput.name = this.dataset.name;
      } else {
        console.error('<ui-select> must have a name attribute');
      }

      if (this.dataset.form) {
        hiddenInput.setAttribute('form', this.dataset.form);
      }

      const inner = document.createElement('div');
      inner.classList.add('ui-select__inner');
      inner.appendChild(svgIcon);
      inner.appendChild(trigger);
      inner.appendChild(itemsContainer);
      inner.appendChild(hiddenInput);

      this.replaceChildren(inner);
      this.attachEvents();

      /** @type {HTMLButtonElement} */
      // @ts-expect-error
      this.trigger = this.querySelector('.ui-select__selected');

      /** @type {HTMLInputElement} */
      // @ts-expect-error
      this.hiddenInput = this.querySelector('input[type="hidden"]');

      this.setAttribute('data-initialized', 'true');
    }

    newId(suffix = null) {
      return (
        'ui-select__' +
        Math.random().toString(36).slice(2, 9) +
        (suffix ? `--${suffix}` : '')
      );
    }

    attachEvents() {
      this.querySelectorAll('.ui-select__item').forEach(el => {
        el.addEventListener('click', e => {
          // @ts-ignore
          this.selectItem(e.currentTarget);
        });
      });
      this.addEventListener('keydown', this.handleKeyDown);
    }

    get value() {
      return this.getAttribute('value') || '';
    }

    /**
     * @param {string} value
     */
    setValue(value) {
      /** @type {NodeListOf<HTMLElement>} */
      const items = this.querySelectorAll('.ui-select__item');

      for (const item of items) {
        if (item.getAttribute('value') === value) {
          this.selectItem(item);
          return;
        }
      }
    }

    /**
     * @param {HTMLElement} item
     */
    selectItem(item) {
      const items = this.querySelectorAll('.ui-select__item');
      const value = item.getAttribute('value') || '';

      const currentValue = this.querySelector(
        '.ui-select__item[aria-selected="true"]',
      );

      if (currentValue && currentValue.getAttribute('value') === value) {
        return;
      }

      /**
       * @type {HTMLElement}
       */
      // @ts-ignore
      const selectedEl = this.querySelector('.ui-select__selected');

      for (const item of items) {
        if (item.getAttribute('value') === value) {
          selectedEl.innerHTML = item.innerHTML;
          item.setAttribute('aria-selected', 'true');
          this.hiddenInput.value = value;
        } else {
          item.setAttribute('aria-selected', 'false');
        }
      }

      this.setAttribute('value', value);
      const event = new CustomEvent('change', { detail: value, bubbles: true });
      this.dispatchEvent(event);

      const groupId = this.getAttribute('sync');

      if (groupId) {
        /** @type {NodeListOf<UISelect>} */
        const selects = document.querySelectorAll(
          `ui-select[sync="${groupId}"]`,
        );

        for (const select of selects) {
          if (select !== this) {
            // console.log('SYNCING', select);
            select.setValue(value);
          }
        }
      }
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleDocumentKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
    }

    /**
     * @param {MouseEvent} event
     */
    handleDocumentClick(event) {
      /** @type {HTMLElement} */
      // @ts-ignore
      const target = event.target;

      if (target.closest('ui-select') === this) {
        return;
      }

      this.close();
    }

    getFirstVisibleItem() {
      /** @type {NodeListOf<HTMLElement>} */
      const items = this.querySelectorAll('[role="option"]');
      for (const item of items) {
        if (item.style.display !== 'none') {
          return item;
        }
      }
      return null;
    }

    /** @param {HTMLElement} item */
    getPrevAndNextItemCandidates(item) {
      /** @type {HTMLElement|null} */
      // @ts-expect-error
      let nextItem = item.nextElementSibling;

      /** @type {HTMLElement|null} */
      // @ts-expect-error
      let prevItem = item.previousElementSibling;

      while (nextItem) {
        if (nextItem.style.display !== 'none') {
          break;
        }
        // @ts-expect-error
        nextItem = nextItem.nextElementSibling;
      }

      while (prevItem) {
        if (prevItem.style.display !== 'none') {
          break;
        }

        // @ts-expect-error
        prevItem = prevItem.previousElementSibling;
      }
      return [prevItem, nextItem];
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleKeyDown(event) {
      /** @type {HTMLElement} */
      // @ts-ignore
      const target = event.target;

      if (this.isOpen() && event.key === 'Escape') {
        this.close();
        this.trigger.focus();
        return;
      }

      if (target === this.trigger) {
        if (!this.isOpen() && (event.key === 'Enter' || event.key === ' ')) {
          this.open();
          this.focusFirstOrSelectedItem();
          event.preventDefault();
          return;
        }

        if (this.isOpen() && (event.key === 'Enter' || event.key === ' ')) {
          this.close();
          this.focus();
          event.preventDefault();
          return;
        }

        if (this.isOpen() && event.key === 'Tab') {
          event.preventDefault();
          return;
        }
      }

      if (target.matches('.ui-select__item')) {
        if (event.key === 'Tab') {
          event.preventDefault();
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          this.selectItem(target);
          this.close();
          event.preventDefault();
          this.trigger.focus();
          return;
        }

        if (event.key === 'ArrowUp') {
          /** @type {HTMLElement|null} */
          // @ts-expect-error
          const prevItem = target.previousElementSibling;
          if (prevItem) {
            prevItem.focus();
          }
          event.preventDefault();
          return;
        }

        if (event.key === 'ArrowDown') {
          /** @type {HTMLElement|null} */
          // @ts-expect-error
          const nextItem = target.nextElementSibling;
          if (nextItem) {
            nextItem.focus();
          }
          event.preventDefault();
          return;
        }
      }
    }

    focusFirstOrSelectedItem() {
      let item = this.querySelector("[role='option'][aria-selected='true']");

      if (item == null || item.style.display === 'none') {
        item = this.getFirstVisibleItem();
      }

      item?.focus();
    }

    isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    onClick() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
        this.focusFirstOrSelectedItem();
      }
    }

    open() {
      const value = this.getAttribute('value') || '';
      /**
       * @type {NodeListOf<HTMLElement>} items
       */
      const items = this.querySelectorAll('.ui-select__item');
      const preserve = this.hasAttribute('preserve');

      for (const item of items) {
        if (preserve) {
          continue;
        }

        if (item.getAttribute('value') === value) {
          item.style.display = 'none';
        } else {
          item.style.display = '';
        }
      }

      document.body.addEventListener('keydown', this.handleDocumentKeyDown);
      document.body.addEventListener('click', this.handleDocumentClick);
      this.trigger.setAttribute('aria-expanded', 'true');
      this.setAttribute('data-state', 'opened');
    }

    close() {
      document.body.removeEventListener('keydown', this.handleDocumentKeyDown);
      document.body.removeEventListener('click', this.handleDocumentClick);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.setAttribute('data-state', 'closed');
    }
  }

  customElements.define('ui-select', UISelect);
}
